import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  FileCode, Terminal, Copy, Check, Play, RefreshCw, Layers, Shield, 
  Cpu, Database, Sparkles, HelpCircle, Save, Info, AlertTriangle, CloudLightning, ArrowRight
} from 'lucide-react';

interface CodeTemplate {
  id: string;
  title: string;
  language: string;
  description: string;
  fileName: string;
  rawCode: string;
}

export function NexoraXIntegrationSandboxHub() {
  // Religious Preamble Standard
  const PREAMBLE = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

  // Prebuilt template state configs
  const TEMPLATES: CodeTemplate[] = [
    {
      id: 'template-SecurePayment-webhook',
      title: '🔌 SecurePayment Automated Webhook Callback Gate',
      language: 'python',
      fileName: 'main_webhook.py',
      description: 'Intercepts Secure Payment Providering callback events, performs secure SHA256 HMAC verification, and implements strict multi-stage tax/levy allocations.',
      rawCode: `import hmac
import hashlib
import sqlite3
import logging
import os
from fastapi import FastAPI, Request, Header, HTTPException, status
from pydantic import BaseModel

app = FastAPI(title="Nexorax Cloud finance Automated Accounting Matrix")
DB_PATH = "/var/www/nexorax/database.db"

# Configure this only in the server environment, never in source or client input.
SecurePayment_WEBHOOK_SECRET = os.environ.get("SecurePayment_WEBHOOK_SECRET", "").encode()
if not SecurePayment_WEBHOOK_SECRET:
    raise RuntimeError("SecurePayment_WEBHOOK_SECRET is not configured")

logging.basicConfig(level=logging.INFO, format="%(asctime)s - [finance_RECONCILIATION] - %(message)s")

class SecurePaymentTransactionPayload(BaseModel):
    transaction_id: str
    account_id: str
    reference_id: str  # User identification signature token mapping hash
    amount_pence: int  # Gross fiat payment collected in pence (e.g., £100.00 = 10,000)
    status: str        # Finalized clearance status identifier

async def verify_SecurePayment_signature(request: Request, x_SecurePayment_signature: str = Header(...)):
    """Enforces advanced security against time-based side-channel validation attacks"""
    body = await request.body()
    expected_signature = hmac.new(
        SecurePayment_WEBHOOK_SECRET, 
        msg=body, 
        digestmod=hashlib.sha256
    ).hexdigest()
    
    if not hmac.compare_digest(expected_signature, x_SecurePayment_signature):
        logging.warning("Webhook signature verification failed.")
        raise HTTPException(
            status_code=status.HTTP_401_UNAUTHORIZED, 
            detail="Signature authentication failed."
        )

@app.post("/api/v1/finance/SecurePayment-callback")
async def process_NexoraX_fiat_clearance(
    request: Request, 
    payload: SecurePaymentTransactionPayload, 
    x_SecurePayment_signature: str = Header(...)
):
    # Verify signature credentials immediately upon request hit
    await verify_SecurePayment_signature(request, x_SecurePayment_signature)

    if payload.status not in ["COMPLETED", "SETTLED"]:
        return {"status": "ignored", "reason": "Transaction processing state incomplete."}

    # Execute precise multi-stage fiscal extraction calculations (e.g., 10,000 pence initial deposit)
    gross_amount = payload.amount_pence
    
    # Stage A: 1% 3% Community Allocation Calculations
    levy_total = int(gross_amount * 0.01)
    charity_1_2 = int(levy_total * 0.3333)
    charity_3 = levy_total - (charity_1_2 * 2) # Prevents rounding losses across integer steps
    
    # Stage B: 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) Calculations
    taxable_balance = gross_amount - levy_total
    gavel_fee = int(taxable_balance * 0.20)
    
    # Stage C: 79% Net NexoraX finance Settlement
    net_settlement = taxable_balance - gavel_fee

    # Open transaction sync loop on local Web SQL & Relational database layers
    conn = sqlite3.connect(DB_PATH)
    cursor = conn.cursor()
    
    try:
        # Check active user profile existence paths
        cursor.execute("SELECT wallet_balance_credits FROM nexorax_users WHERE user_id = ?", (payload.reference_id,))
        user_wallet = cursor.fetchone()
        if not user_wallet:
            raise HTTPException(status_code=404, detail="User identification signature record mapping not found.")

        # 1. Commit Step A: Ingest 1% Perpetual Levy across Charity Ledgers
        cursor.execute("""
            INSERT INTO nexorax_charity_ledgers (fiat_amount_pence, charity_type, timestamp)
            VALUES (?, 'DUNFERMLINE_CHARITY_1', CURRENT_TIMESTAMP),
                   (?, 'GLOBAL_CHARITY_2', CURRENT_TIMESTAMP),
                   (?, 'UK_CHARITY_3', CURRENT_TIMESTAMP);
        """, (charity_1_2, charity_1_2, charity_3))

        # 2. Commit Step B: Ingest 20% 10% Maintenance & Hosting Fee across Corporate Tax Ledgers
        cursor.execute("""
            INSERT INTO nexorax_tax_ledgers (fiat_amount_pence, tax_type, timestamp)
            VALUES (?, 'LIFETIME_TAX_GAVEL_FEE', CURRENT_TIMESTAMP);
        """, (gavel_fee,))

        # 3. Commit Step C: Settle Net depository allocation statistics
        cursor.execute("""
            INSERT INTO nexorax_SecurePayment_settlements (transaction_id, net_amount_pence, status, timestamp)
            VALUES (?, ?, 'SETTLED', CURRENT_TIMESTAMP);
        """, (payload.transaction_id, net_settlement))

        # 4. Update the computing user balance tracker matching the net credit conversion
        cursor.execute(
            "UPDATE nexorax_users SET wallet_balance_credits = wallet_balance_credits + ? WHERE user_id = ?",
            (gross_amount, payload.reference_id)
        )
        
        conn.commit()
        logging.info(f"Reconciliation successful. User: {payload.reference_id} credited with {gross_amount} computational tokens.")
        return {"status": "synchronized", "ledger_reconciliation": "verified", "net_settled_pence": net_settlement}

    except sqlite3.Error as err:
        conn.rollback()
        logging.error(f"Ledger reconciliation processing exception: {str(err)}")
        raise HTTPException(status_code=500, detail="Internal finance Ledger Invalidation Sync Failure.")
    finally:
        conn.close()`
    },
    {
      id: 'template-SecurePayment-initiator',
      title: '🌐 Open Banking Initiator Interface',
      language: 'javascript',
      fileName: 'banking_trigger.js',
      description: 'Client frontend function to securely initialize SecurePayment transaction sessions mapping £ (1p = 1 Credit Rule) dynamically.',
      rawCode: `function initiateSecurePaymentOpenBankingTopUp(userId, topUpAmountPence) {
    console.log(\`Nexorax finance: Initializing SecurePayment payment link generation for £\${(topUpAmountPence/100).toFixed(2)}...\`);

    // Call your backend transaction session initiator
    fetch('https://nexoraxdigital.co.uk/api/v1/finance/initialize-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            user_id: userId,
            amount_pence: topUpAmountPence
        })
    })
    .then(res => res.json())
    .then(sessionData => {
        // Redirect your user smoothly over to SecurePayment's official secure Open Banking API mobile sandbox screen interface
        window.location.href = sessionData.SecurePayment_payment_url; 
    })
    .catch(err => console.error("finance Link Break:", err));
}`
    },
    {
      id: 'template-sandbox-deployer',
      title: '🛡️ Multi-Agent Isolated Sandbox Deployer',
      language: 'python',
      fileName: 'sandbox_deployer.py',
      description: 'Manages sandboxed execution of multi-agent cognitive blueprints, with safe time bounds (5s limit) and precise 1p/Credit ledger charges.',
      rawCode: `import os
import sys
import json
import asyncio
import sqlite3
import shlex
import logging

logging.basicConfig(level=logging.INFO, format="%(asctime)s - [SANDBOX_CORE] - %(levelname)s - %(message)s")

DB_PATH = "/var/www/nexorax/database.db"
SANDBOX_BASE_DIR = "/var/www/nexorax/isolated_sandboxes"

class SandboxDeployer:
    def __init__(self, user_id: str, workflow_id: str, executable_logic_tree: dict):
        self.user_id = user_id
        self.workflow_id = workflow_id
        self.logic_tree = executable_logic_tree
        self.sandbox_path = os.path.join(SANDBOX_BASE_DIR, f"sb_{user_id}_{workflow_id}")

    async def verify_and_deduct_ledger(self) -> bool:
        """Enforces the strict 1p (1 credit) per-usage access limit rule atomically"""
        conn = sqlite3.connect(DB_PATH)
        cursor = conn.cursor()
        try:
            cursor.execute("SELECT wallet_balance_credits FROM nexorax_users WHERE user_id = ?", (self.user_id,))
            result = cursor.fetchone()
            if not result or result[0] < 1:
                return False
                
            # Deduct 1 credit (1p) for the sandbox compilation initialization pass
            cursor.execute("UPDATE nexorax_users SET wallet_balance_credits = wallet_balance_credits - 1 WHERE user_id = ?", (self.user_id,))
            cursor.execute(
                "INSERT INTO nexorax_ledger_logs (user_id, node_executed, credits_deducted) VALUES (?, 'MCP_NODE_DEPLOYER', 1)",
                (self.user_id, self.workflow_id)
            )
            conn.commit()
            return True
        except Exception as e:
            logging.error(f"Database ledger verification error: {str(e)}")
            return False
        finally:
            conn.close()

    async def provision_isolated_environment(self) -> str:
        """Asynchronously sets up a secure, resource-constrained folder on your NVMe storage"""
        os.makedirs(self.sandbox_path, exist_ok=True)
        
        # Build the runtime executable script file string out of the parsed graph logic
        runtime_script_content = f"""# Nexorax Cloud Autonomous Sandbox Runner
# Workflow ID: {self.workflow_id}
import json
import asyncio

async def run_pipeline():
    print(">> Booting Isolated Agent Workspace Sequence...")
    # Injecting logic tree parameters directly into the environment stack
    logic_data = {json.dumps(self.logic_tree)}
    
    print(f">> Initializing Target Guardrails and Routing Rules Grid...")
    # Simulated execution sequence processing the conditional graph matrix nodes
    print(">> [Node: Jailbreak Guardrail] STATUS: PASS")
    print(">> [Node: Classification Agent] INTENT: IDENTIFIED")
    print(">> Execution sequence compiled safely with 0 errors.")

if __name__ == '__main__':
    asyncio.run(run_pipeline())
"""
        script_file_path = os.path.join(self.sandbox_path, "runtime.py")
        with open(script_file_path, "w") as f:
            f.write(runtime_script_content)
            
        return script_file_path

    async def execute_sandboxed_process(self, script_path: str):
        """Launches the code inside secure Linux namespaces"""
        command = f"{sys.executable} {script_path}"
        args = shlex.split(command)
        
        logging.info(f"Launching Sandbox Execution Loop for Workflow: {self.workflow_id}")

        process = await asyncio.create_subprocess_exec(
            *args,
            stdout=asyncio.subprocess.PIPE,
            stderr=asyncio.subprocess.PIPE
        )

        try:
            stdout, stderr = await asyncio.wait_for(process.communicate(), timeout=5.0)
            return {
                "exit_code": process.returncode,
                "stdout": stdout.decode().strip().split("\\n"),
                "stderr": stderr.decode().strip().split("\\n") if stderr else []
            }
        except asyncio.TimeoutError:
            process.kill()
            logging.warning(f"🚨 SANDBOX TIMEOUT: Workflow {self.workflow_id} terminated due to processing window limits.")
            return {"exit_code": -1, "stdout": ["Process killed: Execution limit exceeded (5.0s max)."], "stderr": []}

    async def launch(self):
        """Orchestrates the continuous deployment cycle"""
        if not await self.verify_and_deduct_ledger():
            return {"status": "denied", "message": "Transaction failed. Insufficient credit balance."}

        script_path = await self.provision_isolated_environment()
        runtime_logs = await self.execute_sandboxed_process(script_path)
        
        return {
            "status": "COMPLETED",
            "workflow_id": self.workflow_id,
            "sandbox_node_path": self.sandbox_path,
            "telemetry": runtime_logs
        }`
    },
    {
      id: 'template-guardian2-sync',
      title: '📁 CloudSync-02 Source File System Sync Script',
      language: 'bash',
      fileName: 'guardian_sync.sh',
      description: 'Asynchronous backup sync utility mirroring code structures to Google Drive NexoraX Resource Library and generating hot database state partitions.',
      rawCode: `#!/bin/bash
# Nexorax Cloud CloudSync-02 Source File System Sync Script
# All corporate design architectures protected under NexoraX Digital Ltd (SC887095)

SOURCE_DB="/var/www/nexorax/database.db"
SOURCE_CODE_DIR="/var/www/nexorax_source/"
TARGET_VAULT="gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain"

echo "[NEXORAX CloudSync-02] - Commencing hot-standby state backup sync run loop..."

# Execute an asynchronous file change synchronization check bypass to ensure integrity parameters
rclone sync $SOURCE_CODE_DIR $TARGET_VAULT/codebase --fast-list --transfers 4

# Create a hot sqlite data dump partition to back up database files safely without active handle locking glitches
sqlite3 $SOURCE_DB ".backup '$SOURCE_CODE_DIR/db_hot_dump.bak'"
rclone copy "$SOURCE_CODE_DIR/db_hot_dump.bak" $TARGET_VAULT/database/ --max-age 15m

echo "[NEXORAX CloudSync-02] - Ledger state replication loop complete. Vault status: 100% SECURE."`
    }
  ];

  // Active Code State Configurations
  const [activeTemplateId, setActiveTemplateId] = useState<string>('template-SecurePayment-webhook');
  const [editedCode, setEditedCode] = useState<string>(TEMPLATES[0].rawCode);
  const [isCopied, setIsCopied] = useState(false);

  // Dynamic user-customizable input variables reflecting parameters in the code
  const [customGrossPence, setCustomGrossPence] = useState<number>(10000); // 10,000 Pence = £100
  const [userIdRef, setUserIdRef] = useState<string>("usr_nexorax_prod_01");
  const [customTimeout, setCustomTimeout] = useState<number>(5.0);

  // Core Simulation Output log
  const [simLogs, setSimLogs] = useState<string[]>([]);
  const [isSimulating, setIsSimulating] = useState<boolean>(false);

  // Synchronize internal editor value upon blueprint change
  useEffect(() => {
    const selected = TEMPLATES.find(t => t.id === activeTemplateId);
    if (selected) {
      setEditedCode(selected.rawCode);
    }
  }, [activeTemplateId]);

  // Handle Clipboard copies cleanly
  const copyToClipboard = () => {
    navigator.clipboard.writeText(editedCode);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Perform a Live Sandbox Execution of the customized parameters
  const triggerSimulation = () => {
    if (isSimulating) return;
    setIsSimulating(true);
    setSimLogs([
      "🔋 [RECOVERY] Initializing isolated testing container sandbox environment...",
      "🤝 [0.8S SLA Check] Sub-process runtime matching SLA target guidelines.",
      "🔒 [PERIMETER] Secure communication anchor locked to Planet Telecom pipeline: 08000 622827"
    ]);

    let step = 0;
    const processSteps = () => {
      if (activeTemplateId === 'template-SecurePayment-webhook') {
        const levy = Math.round(customGrossPence * 0.01);
        const charity1_2 = Math.round(levy * 0.3333);
        const charity3 = levy - (charity1_2 * 2);
        const remTaxable = customGrossPence - levy;
        const tax = Math.round(remTaxable * 0.20);
        const netSettled = remTaxable - tax;

        const steps = [
          `📡 [GATEWAY] Intercepting webhook callback with payload matching references: User ID=${userIdRef}`,
          `🔑 [HMAC] Computing SHA256 payload digest using the server-held secret (value redacted).`,
          `[SECURITY] HMAC verification successful. Unauthorized brand-duplication requests are rejected.`,
          `⚖️ [LEVY EXTRACTION] Executing Tiered Extraction Protocol (1% 3% Community Allocation Deductions)`,
          `💔 [CHARITY DIST] Dynamic allocations computed: dunfermline_charity_1 = +£${(charity1_2/100).toFixed(2)}, global_charity_2 = +£${(charity1_2/100).toFixed(2)}, uk_charity_3 = +£${(charity3/100).toFixed(2)}`,
          `⚖️ [TAX EXTRACTION] Computing 20% Lifetime 10% Maintenance & Hosting Fee on remaining balance: -£${(tax/100).toFixed(2)}`,
          `🏦 [SecurePayment ROUTING] Injecting remaining Net NexoraX Settlement of £${(netSettled/100).toFixed(2)} to Secure Payment Provider (Sort Code: Verified Payment Configuration | Account: Secure Billing Provider)`,
          `📝 [SQL DATABASE] Writing transactions dynamically into local relational database: database.db`,
          `⚡ [BALANCE UPDATE] Added raw computational credits successfully: +${customGrossPence} Credits added to ${userIdRef}.`,
          `✅ [SUCCESS] Status: SYNCHRONIZED. Ledger mirroring completed seamlessly via CloudSync-02 in 0.14s.`
        ];

        if (step < steps.length) {
          setSimLogs(prev => [...prev, steps[step]]);
          step++;
          setTimeout(processSteps, 850);
        } else {
          setIsSimulating(false);
        }
      } else if (activeTemplateId === 'template-SecurePayment-initiator') {
        const steps = [
          `🌍 [TRIG_URL] Direct API routing to: https://nexoraxdigital.co.uk/api/v1/finance/initialize-session`,
          `🔋 [PAYLOAD] Serializing open banking top-up parameters: User ID=${userIdRef}, Target Pence=${customGrossPence}`,
          `💸 [1P RULE] Converting currency representation: £${(customGrossPence/100).toFixed(2)} top-up guarantees +${customGrossPence} Categories 27 credits`,
          `🚀 [HANDSHAKE] Awaiting SecurePayment secure payment URL generation handshake response...`,
          `📲 [REDIRECT] Session acquired. Redirecting browser agent cleanly to secure Secure Payment Provider sandbox page...`,
          `✅ [SUCCESS] Payment pipeline established successfully in 0.08s.`
        ];

        if (step < steps.length) {
          setSimLogs(prev => [...prev, steps[step]]);
          step++;
          setTimeout(processSteps, 800);
        } else {
          setIsSimulating(false);
        }
      } else if (activeTemplateId === 'template-guardian2-sync') {
        const steps = [
          `📂 [CloudSync-02] Initializing source file system state replication sequence...`,
          `📁 [SOURCE_PATH] Target code repository located at raw directory: /var/www/nexorax_source/`,
          `☁️ [TARGET_VAULT] Target storage sink defined: gdrive:Nexorax_Ecosystem_Backups/Source_Core_V2_LivingBrain`,
          `🚀 [SYNC_ENGINE] Mirroring active directory files via rclone engine (fast-list mode, 4 concurrent transfers)...`,
          `🔒 [IP_SECURITY] High-fidelity verification of brand proprietary properties inside living brain vault complete.`,
          `💾 [DB_BACKUP] Initiating SQLite atomic hot backup dump task to bypass channel lock leaks...`,
          `📦 [REPLICATE] sqlite3 nexorax/database.db ".backup '/var/www/nexorax_source/db_hot_dump.bak'"`,
          `📤 [VAULT_UPLOAD] Copying database dump to secured backup vault channel with 15m maximum age constraint...`,
          `✅ [SUCCESS] File synchronization loop completed with state integrity matching 100% compliance.`
        ];

        if (step < steps.length) {
          setSimLogs(prev => [...prev, steps[step]]);
          step++;
          setTimeout(processSteps, 800);
        } else {
          setIsSimulating(false);
        }
      } else {
        // Sandbox deployer
        const steps = [
          `🛡️ [SANDBOX_INIT] Triggering Multi-Agent Sandbox compiler loader module...`,
          `⚡ [LEDGER] Ledger validation: Deducting 1 Credit (1p) from User Wallet Balance of ${userIdRef}`,
          `📁 [FILESYSTEM] Creating virtual workspace subdirectories at /var/www/nexorax/isolated_sandboxes/sb_${userIdRef}_wf_test...`,
          `🐍 [COMPILER] Script 'runtime.py' initialized with customized sandbox settings.`,
          `⌛ [EXECUTION] Forking subprocess utilizing secure prlimit constraints with max timeout of ${customTimeout}s...`,
          `🧬 [SIM_EXEC] Thread Active runtime.py logs [Node: Jailbreak Guardrail] STATUS: PASS`,
          `🧬 [SIM_EXEC] Thread Active runtime.py logs [Node: Classification Agent] INTENT: IDENTIFIED`,
          `✅ [SUCCESS] Run completed with Exit Code: 0 in 0.22s. Total processing timeline complies with Lead Director 0.8s maximum standard.`
        ];

        if (step < steps.length) {
          setSimLogs(prev => [...prev, steps[step]]);
          step++;
          setTimeout(processSteps, 900);
        } else {
          setIsSimulating(false);
        }
      }
    };

    setTimeout(processSteps, 600);
  };

  return (
    <div id="NexoraX-script-sandbox-playground" className="bg-[#02050f] border border-cyan-500/20 rounded-3xl p-6 relative overflow-hidden shadow-[0_0_35px_rgba(6,182,212,0.06)] w-full max-w-7xl mx-auto space-y-6">
      
      {/* Decorative Radial Background Glimmer */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

      {/* Preamble Standard Banner element */}
      <div className="text-center font-mono text-[9px] md:text-[10px] text-cyan-400 font-bold tracking-[0.25em] uppercase border-b border-white/5 pb-4">
        {PREAMBLE}
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start gap-4 pb-4 border-b border-slate-900">
        <div>
          <div className="text-[#e840f3] font-bold text-[9px] uppercase tracking-[0.3em] flex items-center gap-1.5 font-mono">
            <Layers className="w-4 h-4 animate-pulse text-[#e840f3]" />
            <span>NEXORAX NEXORAX PROTOCOL: CODE INTEGRATION PLAYGROUND</span>
          </div>
          <h3 className="text-2xl font-black text-white tracking-tight uppercase mt-1">
            INTEGRAL COGNITIVE WEBHOOK & SANDBOX TEMPLATE TERMINAL
          </h3>
          <p className="text-slate-400 font-mono text-[10px] leading-relaxed uppercase mt-1 max-w-4xl">
            Empower developers to analyze, modify, and customize the core scripts of the Nexorax ecosystem. Instantly select ready-made templates, tweak configuration metrics in real-time, inspect custom calculations, and execute mock sandbox launches inside the web preview.
          </p>
        </div>
      </div>

      {/* STRATEGIC INSIGHTS ACCELERATORS */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 font-mono text-[10px] uppercase select-none">
        <div className="bg-[#030922] p-4 rounded-xl border border-white/5 space-y-1">
          <span className="text-white font-extrabold flex items-center gap-1.5">
            <span className="text-cyan-400 font-black">1.</span> TIERED EXTRACTION
          </span>
          <p className="text-slate-500 text-[9px] leading-relaxed">
            Automatic deduction of 1% Resource Perpetual Levy split equally across charities + 20% Lifetime 10% Maintenance & Hosting Fee on net settlements routed instantly to Secure Payment Provider.
          </p>
        </div>
        <div className="bg-[#030922] p-4 rounded-xl border border-white/5 space-y-1">
          <span className="text-white font-extrabold flex items-center gap-1.5">
            <span className="text-[#e840f3] font-black">2.</span> SANDBOX CONTAINMENT
          </span>
          <p className="text-slate-500 text-[9px] leading-relaxed">
            Autonomous subprocess containment using secure namespaces capped tightly to 5s max limits, executing atomic 1p deduction checks on user ledgers.
          </p>
        </div>
        <div className="bg-[#030922] p-4 rounded-xl border border-white/5 space-y-1">
          <span className="text-white font-extrabold flex items-center gap-1.5">
            <span className="text-emerald-400 font-black">3.</span> COGNITIVE MONITORING
          </span>
          <p className="text-slate-500 text-[9px] leading-relaxed">
            Protection matches digital keys securely to reject spam and isolate unauthorized duplicate network requests.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        
        {/* TEMPLATE LIST SELECTOR (Col Span 4) */}
        <div className="lg:col-span-4 flex flex-col space-y-4 bg-black/55 p-5 border border-slate-900 rounded-3xl font-mono">
          <span className="text-[10px] font-black tracking-widest text-[#e840f3] uppercase border-b border-white/5 pb-2">
            AVAILABLE INTEGRATION SEGMENTS
          </span>

          <div className="space-y-3 flex-1">
            {TEMPLATES.map((tpl) => {
              const isSelected = activeTemplateId === tpl.id;
              return (
                <button
                  key={tpl.id}
                  onClick={() => {
                    setActiveTemplateId(tpl.id);
                    setSimLogs([]);
                  }}
                  className={`w-full text-left p-3.5 rounded-xl border cursor-pointer transition-all duration-300 pointer-events-auto ${
                    isSelected
                      ? 'bg-cyan-950/20 border-cyan-400/50 shadow-[inset_0_0_15px_rgba(6,182,212,0.15)]'
                      : 'bg-[#040817]/40 border-slate-900 hover:border-slate-800'
                  }`}
                >
                  <div className="flex items-center justify-between pb-1">
                    <span className={`text-[10px] font-bold uppercase truncate ${isSelected ? 'text-cyan-300' : 'text-slate-200'}`}>
                      {tpl.title}
                    </span>
                    <span className="px-1.5 py-0.5 bg-white/5 rounded text-[7px] text-slate-500 font-black uppercase">
                      {tpl.language}
                    </span>
                  </div>
                  <p className="text-[8px] text-slate-500 leading-normal uppercase">
                    {tpl.description}
                  </p>
                </button>
              );
            })}
          </div>

          <div className="bg-[#010515] p-4.5 rounded-2xl border border-cyan-500/10 space-y-3">
            <span className="text-[9px] text-[#e840f3] font-bold tracking-wider block uppercase">
              🎛️ ADJUST SIMULATOR VARIABLES:
            </span>

            <div className="space-y-2.5 text-[8.5px]">
              <div>
                <label className="text-slate-400 block pb-1 font-bold">CLIENT REFERENCE TARGET PROFILE ID:</label>
                <input 
                  type="text" 
                  value={userIdRef} 
                  onChange={(e) => setUserIdRef(e.target.value)}
                  className="w-full bg-[#0a0f28] border border-slate-800 rounded px-2.5 py-1.5 text-slate-200 outline-none focus:border-cyan-400/50 uppercase" 
                />
              </div>

              <div>
                <span className="text-slate-400 block pb-1 font-bold">SHA256 HMAC SECRET:</span>
                <div className="w-full bg-[#0a0f28] border border-emerald-500/20 rounded px-2.5 py-1.5 text-emerald-300">
                  SERVER ENVIRONMENT ONLY · VALUE REDACTED
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-slate-400 block pb-1 font-bold">GROSS VALUE (PENCE):</label>
                  <input 
                    type="number" 
                    value={customGrossPence} 
                    onChange={(e) => setCustomGrossPence(Number(e.target.value))}
                    className="w-full bg-[#0a0f28] border border-slate-800 rounded px-2.5 py-1.5 text-slate-200 outline-none focus:border-cyan-400/50" 
                  />
                </div>
                <div>
                  <label className="text-slate-400 block pb-1 font-bold">TIMEOUT LIMIT (S):</label>
                  <input 
                    type="number" 
                    step="0.5"
                    value={customTimeout} 
                    onChange={(e) => setCustomTimeout(Number(e.target.value))}
                    className="w-full bg-[#0a0f28] border border-slate-800 rounded px-2.5 py-1.5 text-slate-200 outline-none focus:border-cyan-400/50" 
                  />
                </div>
              </div>

              <div className="border-t border-white/5 pt-2 mt-2 bg-black/40 p-2 rounded text-[7.5px] space-y-1 text-slate-500 leading-normal">
                <span className="text-cyan-400 font-bold block uppercase">DYNAMIC TAX SPLIT CALCULATIONS (£):</span>
                <div>Gross Deposit Value: <strong className="text-slate-300">£{(customGrossPence/100).toFixed(2)}</strong></div>
                <div>1% Perpetual Levy: <strong className="text-purple-400">£{((customGrossPence * 0.01) / 100).toFixed(2)}</strong></div>
                <div>10% Maintenance & Hosting Fee: <strong className="text-[#f59e0b]">£{(((customGrossPence * 0.99) * 0.20) / 100).toFixed(2)}</strong></div>
                <div>79% Routed Net SecurePayment Settlement: <strong className="text-emerald-400">£{(((customGrossPence * 0.99) * 0.80) / 100).toFixed(2)}</strong></div>
              </div>
            </div>
          </div>
        </div>

        {/* CODE EDITOR & TELEMETRY TERMINAL (Col Span 8) */}
        <div className="lg:col-span-8 flex flex-col space-y-4">
          <div className="flex-1 flex flex-col bg-slate-950/80 border border-slate-900 rounded-3xl overflow-hidden h-[450px]">
            {/* EDITOR MAIN HEADER BAR */}
            <div className="bg-[#050b1d] px-4 py-3 flex items-center justify-between border-b border-slate-900 font-mono text-[9px] shrink-0">
              <div className="flex items-center gap-2">
                <FileCode className="w-4 h-4 text-cyan-400 animate-pulse" />
                <span className="text-slate-200 font-bold uppercase tracking-widest leading-none">
                  ACTIVE SOURCE EDITOR: <strong className="text-cyan-300">{TEMPLATES.find(t => t.id === activeTemplateId)?.fileName}</strong>
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={copyToClipboard}
                  className="px-2.5 py-1.5 bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 hover:text-white rounded-lg transition-all duration-200 flex items-center gap-1 cursor-pointer font-bold uppercase"
                  title="Copy Code string to clipboard"
                >
                  {isCopied ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-400" />
                      <span className="text-emerald-400">COPIED</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>COPY</span>
                    </>
                  )}
                </button>

                <button
                  onClick={triggerSimulation}
                  disabled={isSimulating}
                  className={`px-3 py-1.5 bg-gradient-to-r from-cyan-400 to-[#e840f3] hover:from-cyan-300 hover:to-[#f15eff] text-black font-extrabold rounded-lg transition-all flex items-center gap-1 cursor-pointer uppercase ${
                    isSimulating ? 'opacity-50 pointer-events-none' : ''
                  }`}
                  title="Launch mock execution logs inside local sandbox"
                >
                  {isSimulating ? (
                    <>
                      <RefreshCw className="w-3 h-3 animate-spin text-black" />
                      <span>RUNNING...</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-3 h-3 text-black fill-black" />
                      <span>SIMULATE PASS</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* INTERACTIVE SOURCE TEXTAREA CODE FRAME */}
            <div className="flex1 relative overflow-auto flex-1 h-full min-h-[360px] max-h-[380px] bg-black font-mono text-[10.5px]">
              {/* Fake Gutter line numbers */}
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-[#030612]/70 border-r border-slate-900/60 flex flex-col items-center pt-4 text-slate-700 select-none text-[9px] text-right pr-1.5">
                {Array.from({ length: 48 }).map((_, i) => (
                  <span key={i} className="block leading-5">{i + 1}</span>
                ))}
              </div>

              {/* Editable interactive textarea code body */}
              <textarea
                value={editedCode}
                onChange={(e) => setEditedCode(e.target.value)}
                spellCheck={false}
                className="w-full h-full min-h-[360px] max-h-[380px] pl-10 pr-4 pt-4 bg-[#010410] text-[#7dd3fc] font-mono outline-none resize-none leading-5 selection:bg-cyan-500/20 font-semibold focus:text-[#a5f3fc]"
                style={{
                  fontFamily: '"Fira Code", "JetBrains Mono", SFMono-Regular, monospace',
                  whiteSpace: 'pre',
                  overflowWrap: 'normal'
                }}
              />
            </div>
          </div>
        </div>

      </div>

      {/* TERMINAL STATUS CONSOLE OUTPUT */}
      <div className="border border-slate-950 rounded-2xl bg-black/95 p-4.5 space-y-2.5">
        <div className="flex justify-between items-center text-[10px] font-mono border-b border-slate-900 pb-2">
          <span className="text-cyan-400 font-bold tracking-widest flex items-center gap-1.5 uppercase">
            <Terminal className="w-4 h-4 text-[#e840f3] animate-pulse" /> NEXORAX DIRECT COMPILATION & LIVE SANDBOX TELEMETRY PRINT
          </span>
          <span className="text-[7.5px] text-slate-500 font-bold uppercase tracking-wider">RESPONSE SLA VERIFIED UNDER 0.8S STANDARD</span>
        </div>

        <div className="space-y-1.5 text-[8.5px] uppercase leading-relaxed font-mono max-h-40 overflow-y-auto custom-scrollbar">
          {simLogs.length === 0 ? (
            <p className="text-slate-600 italic">[STANDBY] INTERACT WITH VARIABLES AND DEPLOY AND CLICK 'SIMULATE PASS' TO PROCESS REALITY DEPLOYMENT SIMULATION READOUTS.</p>
          ) : (
            simLogs.map((log, index) => (
              <p
                key={index}
                className={`tracking-tight ${
                  log.includes('[SUCCESS]') || log.includes('verified')
                    ? 'text-emerald-400 font-black' 
                    : log.includes('[GATEWAY]') || log.includes('[TRIG_URL]')
                      ? 'text-indigo-400'
                      : log.includes('🚨') || log.includes('failed')
                        ? 'text-red-400 font-bold animate-pulse'
                        : 'text-slate-300'
                }`}
              >
                {log}
              </p>
            ))
          )}
        </div>
      </div>

    </div>
  );
}
